
import React, { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { db, signOut, auth } from './firebase';
import { collection, query, where, onSnapshot, orderBy, Timestamp, doc, getDocs, limit, getDoc } from 'firebase/firestore';
import { UserProfile, StudentConnection, ClassRoom, Organization, AcademicYear, Message, Announcement, WeeklyLog, Homework, StudentHomeworkStatus, StuckLog, AttendanceRecord, CalendarEvent, TutorLog, FamilyConnectPrompt, Kudo, InvitationCode, ClassInsight, PreReportedAbsence, HomeworkTask, InvitationType, Gender, AssessmentEvent, AssessmentResult, Role, OwnershipTransferRecord } from './types';
import { LoginScreen, OnboardingFlow } from './components/auth';
import { LandingPage } from './components/LandingPage';
import { PrincipalManager } from './components/principal';
import { TeacherManager } from './components/teacher';
import { ParentManager } from './components/parent';
import { SuperAdminManager } from './components/superAdmin';
import { PendingOrgScreen } from './components/pendingOrg';
import { LoadingScreen, ImageViewerModal } from './components/common';
import { addDocument, updateDocument, deleteDocument, setDocument, uploadImageFromBase64, deleteFileFromStorage, getOrgApiKey } from './firebaseUtils';
import { ROOT_SUPER_ADMIN_EMAILS } from './lib/constants';
import { generateContentWithRetry, translateContent, setActiveOrgApiKey } from './lib/gemini';
import { Type } from '@google/genai';

export const App = () => {
  const { firebaseUser, authLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [appDataLoading, setAppDataLoading] = useState(true);
  
  // Data States
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [students, setStudents] = useState<StudentConnection[]>([]);
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [weeklyLogs, setWeeklyLogs] = useState<WeeklyLog[]>([]);
  const [homeworks, setHomeworks] = useState<Homework[]>([]);
  const [studentHomeworkStatuses, setStudentHomeworkStatuses] = useState<StudentHomeworkStatus[]>([]);
  const [stuckLogs, setStuckLogs] = useState<StuckLog[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [tutorLogs, setTutorLogs] = useState<TutorLog[]>([]);
  const [familyConnectPrompts, setFamilyConnectPrompts] = useState<FamilyConnectPrompt[]>([]);
  const [kudos, setKudos] = useState<Kudo[]>([]);
  const [classInsights, setClassInsights] = useState<ClassInsight[]>([]);
  const [preReportedAbsences, setPreReportedAbsences] = useState<PreReportedAbsence[]>([]);
  
  // Assessment Data States
  const [assessmentEvents, setAssessmentEvents] = useState<AssessmentEvent[]>([]);
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResult[]>([]);
  const [invitationCodes, setInvitationCodes] = useState<InvitationCode[]>([]);

  const [simulatedView, setSimulatedView] = useState<{ role: Role; orgId?: string } | null>(null);
  const [viewingImageUrl, setViewingImageUrl] = useState<string | null>(null);

  // 1. Fetch Current User Profile Specific Document (Faster than collection query)
  useEffect(() => {
    if (!firebaseUser) {
      setCurrentUser(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    // Subscribe only to the specific user document to quickly determine identity
    const unsub = onSnapshot(doc(db, 'users', firebaseUser.uid), (docSnap) => {
        if (docSnap.exists()) {
            setCurrentUser({ id: docSnap.id, ...docSnap.data() } as UserProfile);
        } else {
            setCurrentUser(null); // User not found in DB -> Onboarding
        }
        setProfileLoading(false);
    }, (error) => {
        console.error("Error fetching user profile:", error);
        setProfileLoading(false);
    });

    return () => unsub();
  }, [firebaseUser]);

  // 2. Fetch Application Data based on Role/Org
  useEffect(() => {
    if (!currentUser) return;

    setAppDataLoading(true); // Start loading app data
    const unsubs: (() => void)[] = [];

    // Collections to wait for before showing dashboard to avoid flash of content
    const collectionsToLoad = [
        'users', 'students', 'classes', 'organizations', 'academicYears', 
        'messages', 'announcements', 'weeklyLogs', 'homeworks', 
        'studentHomeworkStatuses', 'stuckLogs', 'attendanceRecords', 
        'calendarEvents', 'tutorLogs', 'familyConnectPrompts', 
        'kudos', 'classInsights', 'preReportedAbsences',
        'assessmentEvents', 'assessmentResults'
    ];
    
    let loadedCount = 0;
    // Helper to track when initial data load is complete for a collection
    const checkLoaded = () => {
        loadedCount++;
        if (loadedCount >= collectionsToLoad.length) {
            setAppDataLoading(false);
        }
    };

    // Helper to subscribe to collections
    const subscribe = <T,>(colName: string, setter: React.Dispatch<React.SetStateAction<T[]>>, constraints: any[] = []) => {
        const q = query(collection(db, colName), ...constraints);
        let initialized = false;
        return onSnapshot(q, (snap) => {
            const data = snap.docs.map(doc => {
                const d = doc.data();
                // Convert timestamps back to dates for internal use
                for (const key in d) {
                    if (d[key] instanceof Timestamp) {
                        d[key] = d[key].toDate();
                    }
                }
                return { id: doc.id, ...d } as T;
            });
            setter(data);
            
            if (!initialized) {
                initialized = true;
                checkLoaded();
            }
        }, (error) => {
            console.error(`Error subscribing to ${colName}:`, error);
            if (!initialized) {
                initialized = true;
                checkLoaded(); // Ensure we don't hang on error
            }
        });
    };

    unsubs.push(subscribe('users', setUsers)); // Fetch all users now, after we know who we are
    unsubs.push(subscribe('students', setStudents));
    unsubs.push(subscribe('classes', setClasses));
    unsubs.push(subscribe('organizations', setOrganizations));
    unsubs.push(subscribe('academicYears', setAcademicYears));
    unsubs.push(subscribe('messages', setMessages, [orderBy('timestamp', 'asc')]));
    unsubs.push(subscribe('announcements', setAnnouncements, [orderBy('timestamp', 'desc')]));
    unsubs.push(subscribe('weeklyLogs', setWeeklyLogs));
    unsubs.push(subscribe('homeworks', setHomeworks));
    unsubs.push(subscribe('studentHomeworkStatuses', setStudentHomeworkStatuses));
    unsubs.push(subscribe('stuckLogs', setStuckLogs));
    unsubs.push(subscribe('attendanceRecords', setAttendanceRecords));
    unsubs.push(subscribe('calendarEvents', setCalendarEvents));
    unsubs.push(subscribe('tutorLogs', setTutorLogs));
    unsubs.push(subscribe('familyConnectPrompts', setFamilyConnectPrompts));
    unsubs.push(subscribe('kudos', setKudos));
    unsubs.push(subscribe('classInsights', setClassInsights));
    unsubs.push(subscribe('preReportedAbsences', setPreReportedAbsences));
    unsubs.push(subscribe('assessmentEvents', setAssessmentEvents, [orderBy('date', 'desc')]));
    unsubs.push(subscribe('assessmentResults', setAssessmentResults));
    unsubs.push(subscribe('invitationCodes', setInvitationCodes));

    return () => {
        unsubs.forEach(u => u());
    };
  }, [currentUser?.id, currentUser?.orgId, currentUser?.role]);

  // 3. Sync Active Organization Gemini API Key (BYOK)
  useEffect(() => {
    let isMounted = true;
    const orgId = simulatedView?.orgId || currentUser?.orgId;
    if (!orgId) {
      setActiveOrgApiKey(null);
      return;
    }

    const activeOrg = organizations.find(o => o.id === orgId);
    
    // First, use in-memory key if already present as instant fallback
    if (activeOrg?.customApiKey) {
      setActiveOrgApiKey(activeOrg.customApiKey);
    }

    // Next, fetch securely from isolated subcollection (takes authoritative precedence)
    getOrgApiKey(orgId).then(secureKey => {
      if (!isMounted) return;
      if (secureKey) {
        setActiveOrgApiKey(secureKey);
      } else if (!activeOrg?.customApiKey) {
        setActiveOrgApiKey(null);
      }
    }).catch(() => {
      if (isMounted && !activeOrg?.customApiKey) {
        setActiveOrgApiKey(null);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [currentUser, simulatedView, organizations]);

  // --- Handlers ---

  const handleUpdateUser = async (userId: string, data: Partial<UserProfile>) => {
      let finalData = { ...data };
      if (finalData.photoUrl && finalData.photoUrl.startsWith('data:')) {
          try {
              // Path: user_photos/{userId}
              const path = `user_photos/${userId}`;
              finalData.photoUrl = await uploadImageFromBase64(finalData.photoUrl, path);
          } catch (e) {
              console.warn("Profile photo storage upload skipped, using fallback", e);
          }
      }
      await updateDocument('users', userId, finalData);
      setCurrentUser(prev => (prev && prev.id === userId) ? { ...prev, ...finalData } : prev);
  };

  const handleCreateOrg = async (name: string, type: any, address: any, photoUrl: any, adminId: string, phone?: string, verification?: any) => {
      let finalPhotoUrl = photoUrl;
      if (photoUrl && typeof photoUrl === 'string' && photoUrl.startsWith('data:')) {
          try {
              const fileName = `${adminId}_${Date.now()}`;
              const path = `org_logos/${fileName}`;
              finalPhotoUrl = await uploadImageFromBase64(photoUrl, path);
          } catch (e) {
              console.warn("Org logo storage upload skipped, using fallback", e);
          }
      }

      let finalDocUrl = verification?.documentUrl;
      if (finalDocUrl && typeof finalDocUrl === 'string' && finalDocUrl.startsWith('data:')) {
          try {
              const docFileName = `doc_${adminId}_${Date.now()}`;
              const path = `verification_docs/${docFileName}`;
              finalDocUrl = await uploadImageFromBase64(finalDocUrl, path);
          } catch (e) {
              console.warn("Verification doc storage upload skipped, using fallback", e);
          }
      }

      // Ensure docUrl and photoUrl do not exceed Firestore payload limits if storage upload was unauthorized
      let safeDocUrl = '';
      if (finalDocUrl && typeof finalDocUrl === 'string') {
          if (finalDocUrl.startsWith('http://') || finalDocUrl.startsWith('https://')) {
              safeDocUrl = finalDocUrl;
          } else if (finalDocUrl.startsWith('data:') && finalDocUrl.length < 200000) {
              safeDocUrl = finalDocUrl;
          } else {
              safeDocUrl = '';
          }
      }

      let safePhotoUrl = '';
      if (finalPhotoUrl && typeof finalPhotoUrl === 'string') {
          if (finalPhotoUrl.startsWith('http://') || finalPhotoUrl.startsWith('https://')) {
              safePhotoUrl = finalPhotoUrl;
          } else if (finalPhotoUrl.startsWith('data:') && finalPhotoUrl.length < 250000) {
              safePhotoUrl = finalPhotoUrl;
          } else {
              safePhotoUrl = '';
          }
      }

      const finalVerification = verification ? {
          affiliationNumber: typeof verification.affiliationNumber === 'string' ? verification.affiliationNumber.trim() : '',
          publicUrl: typeof verification.publicUrl === 'string' ? verification.publicUrl.trim() : '',
          documentUrl: safeDocUrl,
          documentName: typeof verification.documentName === 'string' ? verification.documentName.trim() : '',
          officialEmail: typeof verification.officialEmail === 'string' ? verification.officialEmail.trim() : '',
          submittedAt: new Date()
      } : undefined;

      const cleanAddress = {
          street: typeof address?.street === 'string' ? address.street.trim() : '',
          city: typeof address?.city === 'string' ? address.city.trim() : '',
          state: typeof address?.state === 'string' ? address.state.trim() : '',
          country: typeof address?.country === 'string' ? address.country.trim() : ''
      };

      const finalPhone = typeof phone === 'string' ? phone.trim() : (typeof address?.phone === 'string' ? address.phone.trim() : '');

      return await addDocument<Organization>('organizations', { 
          name: typeof name === 'string' && name.trim() ? name.trim() : 'My Institution', 
          type: type || 'school', 
          address: cleanAddress, 
          photoUrl: safePhotoUrl, 
          adminId, 
          phone: finalPhone,
          status: 'pending',
          ...(finalVerification ? { verification: finalVerification } : {}),
          createdAt: new Date(),
          updatedAt: new Date()
      });
  };
  
  const handleUpdateOrg = async (id: string, data: Partial<Organization>) => {
      if (!currentUser) return;
      let finalData = { ...data };
      const existingOrg = organizations.find(o => o.id === id);
      const isPendingOrRejected = !existingOrg?.status || existingOrg?.status === 'pending' || existingOrg?.status === 'rejected';

      if (finalData.photoUrl && typeof finalData.photoUrl === 'string' && finalData.photoUrl.startsWith('data:')) {
          try {
              const fileName = `${currentUser.id}_${Date.now()}`;
              const path = `org_logos/${fileName}`;
              finalData.photoUrl = await uploadImageFromBase64(finalData.photoUrl, path);
          } catch (e) {
              console.warn("Org logo update upload skipped, using fallback", e);
          }
          if (finalData.photoUrl && finalData.photoUrl.startsWith('data:') && finalData.photoUrl.length > 250000) {
              finalData.photoUrl = '';
          }
      }
      if (finalData.verification?.documentUrl && typeof finalData.verification.documentUrl === 'string' && finalData.verification.documentUrl.startsWith('data:')) {
          try {
              const docFileName = `doc_${id}_${Date.now()}`;
              const path = `verification_docs/${docFileName}`;
              finalData.verification.documentUrl = await uploadImageFromBase64(finalData.verification.documentUrl, path);
          } catch (e) {
              console.warn("Verification doc update upload skipped, using fallback", e);
          }
          if (finalData.verification.documentUrl && finalData.verification.documentUrl.startsWith('data:') && finalData.verification.documentUrl.length > 200000) {
              finalData.verification.documentUrl = '';
          }
      }

      // Auto-Reset Option B: If the org is pending/rejected, ensure revision tracking is active
      if (isPendingOrRejected && finalData.verification) {
          const currentRev = existingOrg?.verification?.revisionCount || 1;
          const nextRev = finalData.verification.revisionCount || (currentRev + 1);
          finalData.verification = {
              ...(existingOrg?.verification || {}),
              ...finalData.verification,
              submittedAt: finalData.verification.submittedAt || new Date(),
              lastModifiedAt: new Date(),
              revisionCount: nextRev,
              isRevisedAfterSubmission: true,
              ...(existingOrg?.status === 'rejected' ? { rejectionReason: '' } : {})
          };
          if (existingOrg?.status === 'rejected') {
              finalData.status = 'pending';
          }
      }

      finalData.updatedAt = new Date();
      await updateDocument('organizations', id, finalData);
  };

  // Super Admin Handlers
  const handleApproveOrg = async (orgId: string, adminId?: string) => {
      const org = organizations.find(o => o.id === orgId);
      await updateDocument('organizations', orgId, {
          status: 'active',
          verification: {
              ...(org?.verification || {}),
              reviewedBy: currentUser?.email || 'Super Admin',
              reviewedAt: new Date(),
              rejectionReason: ''
          },
          updatedAt: new Date()
      });
      if (adminId) {
          await updateDocument('users', adminId, { 
              approvalStatus: 'approved',
              updatedAt: new Date()
          });
      }
  };

  const handleRejectOrg = async (orgId: string, reason: string, adminId?: string) => {
      const org = organizations.find(o => o.id === orgId);
      await updateDocument('organizations', orgId, {
          status: 'rejected',
          verification: {
              ...(org?.verification || {}),
              reviewedBy: currentUser?.email || 'Super Admin',
              reviewedAt: new Date(),
              rejectionReason: reason
          },
          updatedAt: new Date()
      });
      if (adminId) {
          await updateDocument('users', adminId, { 
              approvalStatus: 'rejected',
              updatedAt: new Date()
          });
      }
  };

  const handleSuspendOrg = async (orgId: string) => {
      await updateDocument('organizations', orgId, {
          status: 'suspended',
          updatedAt: new Date()
      });
  };

  const handleReactivateOrg = async (orgId: string) => {
      const org = organizations.find(o => o.id === orgId);
      await updateDocument('organizations', orgId, {
          status: 'active',
          updatedAt: new Date()
      });
      if (org?.adminId) {
          await updateDocument('users', org.adminId, { 
              approvalStatus: 'approved',
              updatedAt: new Date()
          });
      }
  };

  const handleUpdateUserRole = async (userId: string, newRole: Role) => {
      await updateDocument('users', userId, {
          role: newRole,
          updatedAt: new Date()
      });
  };

  const handleTransferOwnership = async (params: {
      orgId: string;
      newAdminEmail: string;
      newAdminId?: string;
      newAdminName?: string;
      outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
      reason?: string;
      referenceTicket?: string;
      transferType?: 'self_serve' | 'super_admin_reassignment';
  }) => {
      if (!currentUser) throw new Error("Authentication required.");

      const targetOrg = organizations.find(o => o.id === params.orgId);
      if (!targetOrg) throw new Error("Target organization could not be found.");

      const isRootSuperAdmin = currentUser.role === 'super_admin' || (firebaseUser?.email ? ROOT_SUPER_ADMIN_EMAILS.includes(firebaseUser.email) : false);
      const isCurrentAdmin = targetOrg.adminId === currentUser.id;

      // Security validation: only the active primary principal or super admin can initiate handovers
      if (!isCurrentAdmin && !isRootSuperAdmin) {
          throw new Error("Unauthorized: Only the current primary principal or Super Admin can transfer institution ownership.");
      }

      // Resolve destination administrator identity
      let resolvedNewAdminId = params.newAdminId;
      let resolvedNewAdminName = params.newAdminName;

      if (!resolvedNewAdminId) {
          const matchedUser = users.find(u => u.email?.toLowerCase() === params.newAdminEmail.toLowerCase());
          if (matchedUser) {
              resolvedNewAdminId = matchedUser.id;
              resolvedNewAdminName = resolvedNewAdminName || matchedUser.name;
          }
      }

      // Find previous admin user profile
      const previousAdmin = users.find(u => u.id === targetOrg.adminId);

      // Construct immutable transfer audit record
      const transferRecord: OwnershipTransferRecord = {
          previousAdminId: targetOrg.adminId,
          previousAdminEmail: previousAdmin?.email || '',
          previousAdminName: previousAdmin?.name || '',
          newAdminId: resolvedNewAdminId || '',
          newAdminEmail: params.newAdminEmail.toLowerCase().trim(),
          newAdminName: resolvedNewAdminName || '',
          transferredAt: new Date().toISOString(),
          transferredBy: isRootSuperAdmin ? 'super_admin' : currentUser.id,
          transferType: params.transferType || (isRootSuperAdmin ? 'super_admin_reassignment' : 'self_serve'),
          reason: params.reason || '',
          referenceTicket: params.referenceTicket || '',
          outgoingAdminRole: params.outgoingAdminRole
      };

      const updatedHistory = [...(targetOrg.ownershipHistory || []), transferRecord];

      const orgUpdates: Partial<Organization> & { [key: string]: any } = {
          ownershipHistory: updatedHistory,
          updatedAt: new Date()
      };

      if (resolvedNewAdminId) {
          orgUpdates.adminId = resolvedNewAdminId;
          orgUpdates.pendingAdminEmail = ''; // clear pending invite if existing member
      } else {
          // Successor is not yet registered; invite by email
          orgUpdates.pendingAdminEmail = params.newAdminEmail.toLowerCase().trim();
      }

      // Apply updates to the organization document
      await updateDocument('organizations', targetOrg.id, orgUpdates);

      // If new admin is already an active user in the system, promote them to 'principal'
      if (resolvedNewAdminId) {
          await updateDocument('users', resolvedNewAdminId, {
              role: 'principal',
              orgId: targetOrg.id,
              approvalStatus: 'approved',
              updatedAt: new Date()
          });
      }

      // Adjust the outgoing principal's role based on their handover selection
      if (targetOrg.adminId) {
          if (params.outgoingAdminRole === 'teacher') {
              await updateDocument('users', targetOrg.adminId, {
                  role: 'teacher',
                  updatedAt: new Date()
              });
          } else if (params.outgoingAdminRole === 'vice_principal') {
              // Ensure email is tracked in vicePrincipalEmails
              const currentVpEmails = targetOrg.vicePrincipalEmails || [];
              const outEmail = previousAdmin?.email?.toLowerCase();
              if (outEmail && !currentVpEmails.map(e => e.toLowerCase()).includes(outEmail)) {
                  await updateDocument('organizations', targetOrg.id, {
                      vicePrincipalEmails: [...currentVpEmails, outEmail]
                  });
              }
              await updateDocument('users', targetOrg.adminId, {
                  role: 'teacher', // Vice Principals operate on teacher permissions with VP administrative elevation
                  updatedAt: new Date()
              });
          } else if (params.outgoingAdminRole === 'stepped_down') {
              await updateDocument('users', targetOrg.adminId, {
                  approvalStatus: 'graduated',
                  updatedAt: new Date()
              });
          }
      }
  };

  const handleSendAnnouncement = async (title: string, text: string, imageUrl: string | undefined, classIds: string[], academicYearId: string, orgId: string) => {
      let finalImageUrl = imageUrl;
      if (imageUrl && imageUrl.startsWith('data:')) {
          const path = `announcements/${currentUser?.id}_${Date.now()}`;
          finalImageUrl = await uploadImageFromBase64(imageUrl, path);
      }
      
      await addDocument('announcements', {
          title,
          text,
          imageUrls: finalImageUrl ? [finalImageUrl] : [],
          classIds,
          orgId,
          academicYearId,
          senderId: currentUser?.id,
          senderName: currentUser?.name,
          timestamp: new Date(),
          isEdited: false
      });
  };

  const handleSendMessage = async (messageData: any) => {
      let finalImageUrl = messageData.imageUrl;
      if (messageData.imageUrl && messageData.imageUrl.startsWith('data:')) {
          const path = `chat_images/${currentUser?.id}_${Date.now()}`;
          finalImageUrl = await uploadImageFromBase64(messageData.imageUrl, path);
      }

      await addDocument('messages', {
          ...messageData,
          imageUrl: finalImageUrl,
          timestamp: new Date(),
          readBy: [currentUser?.id]
      });
  };

  const handleUpdateMessage = async (messageId: string, newText: string, targetLanguage: string) => {
      await updateDocument('messages', messageId, {
          text: newText,
          originalText: newText,
          isEdited: true
      });
  };

  const handleDeleteMessage = async (messageId: string) => {
      await deleteDocument('messages', messageId);
  };

  const handleSendKudo = async (kudoData: any) => {
      await addDocument('kudos', {
          ...kudoData,
          timestamp: new Date()
      });
  };

  const handleDeleteAccount = async () => {
      if (window.confirm("Are you sure you want to delete your account? This cannot be undone.")) {
          if (currentUser) {
              await deleteDocument('users', currentUser.id);
              await signOut(auth);
          }
      }
  };

  const handleMarkMessagesRead = async (messageIds: string[]) => {
      if (!currentUser || !messageIds || messageIds.length === 0) return;
      const userId = currentUser.id;
      
      // Optimistic update in local state for instantaneous responsiveness
      setMessages(prev => prev.map(m => {
          if (messageIds.includes(m.id) && !(m.readBy || []).includes(userId)) {
              return { ...m, readBy: [...(m.readBy || []), userId] };
          }
          return m;
      }));

      const unreadMsgs = messages.filter(m => messageIds.includes(m.id) && !(m.readBy || []).includes(userId));
      if (unreadMsgs.length === 0) return;

      const updates = unreadMsgs.map(async (msg) => {
          try {
              await updateDocument('messages', msg.id, {
                  readBy: [...(msg.readBy || []), userId]
              });
          } catch (err) {
              console.warn("Failed to mark message as read:", msg.id, err);
          }
      });
      await Promise.all(updates).catch(() => {});
  };

  const handleSaveWeeklyLog = async (logData: any) => {
      await addDocument('weeklyLogs', {
          ...logData,
      });
  };

  const handleUpdateStudent = async (studentId: string, data: any) => {
      let finalData = { ...data };
      if (data.photoUrl && data.photoUrl.startsWith('data:')) {
           const path = `student_photos/${studentId}_${Date.now()}`;
           finalData.photoUrl = await uploadImageFromBase64(data.photoUrl, path);
      }
      await updateDocument('students', studentId, finalData);
  };

  const handleDeleteStudent = async (studentId: string) => {
      await updateDocument('students', studentId, { approvalStatus: 'removed' });
  };

  const handleUpdateStudentHomeworkStatus = async (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => {
      const existingStatus = studentHomeworkStatuses.find(s => s.studentId === studentId && s.homeworkId === homeworkId);
      if (existingStatus) {
          let newCompleted = [...existingStatus.completedTaskIds];
          if (isComplete) {
              if (!newCompleted.includes(taskId)) newCompleted.push(taskId);
          } else {
              newCompleted = newCompleted.filter(id => id !== taskId);
          }
          await updateDocument('studentHomeworkStatuses', existingStatus.id, { completedTaskIds: newCompleted });
      } else {
          if (isComplete) {
              await addDocument('studentHomeworkStatuses', {
                  studentId,
                  homeworkId,
                  completedTaskIds: [taskId]
              });
          }
      }
  };

  const handleLogStuck = async (studentId: string, homeworkId: string, taskId: string) => {
      const existing = stuckLogs.find(l => l.studentId === studentId && l.homeworkId === homeworkId && l.taskId === taskId);
      if (!existing) {
          await addDocument('stuckLogs', {
              studentId,
              homeworkId,
              taskId,
              timestamp: new Date()
          });
      }
  };

  const handleManageAbsence = async (absenceData: { id?: string, date: string, reason: string, attachment?: string }, studentId: string, isDelete?: boolean) => {
      if (!currentUser) return;
      const student = students.find(s => s.id === studentId);
      if (!student) return;

      if (isDelete && absenceData.id) {
          await deleteDocument('preReportedAbsences', absenceData.id);
          return;
      }

      // Check for duplicate on this date if creating new or changing date
      const existingOnDate = preReportedAbsences.find(p => p.studentId === studentId && p.date === absenceData.date);
      if (existingOnDate && existingOnDate.id !== absenceData.id) {
          alert(`Absence already reported for ${absenceData.date}. Please edit the existing report.`);
          return;
      }

      let finalAttachmentUrl = absenceData.attachment;
      if (absenceData.attachment && absenceData.attachment.startsWith('data:')) {
          const path = `absence_docs/${studentId}_${Date.now()}`;
          try {
              finalAttachmentUrl = await uploadImageFromBase64(absenceData.attachment, path);
          } catch (e) {
              console.error("Failed to upload absence attachment", e);
              alert("Failed to upload attachment, saving without it.");
              finalAttachmentUrl = undefined;
          }
      }

      const payload = {
          studentId,
          classId: student.classId,
          date: absenceData.date,
          reason: absenceData.reason,
          reportedById: currentUser.id,
          attachmentUrl: finalAttachmentUrl
      };

      if (absenceData.id) {
          await updateDocument('preReportedAbsences', absenceData.id, payload);
      } else {
          await addDocument('preReportedAbsences', payload);
      }
  };

  const handleGetTutorHelp = async (studentId: string, homeworkId: string, taskId: string, task: HomeworkTask, studentName: string, existingLog: TutorLog | null, userInput?: string, triggerType?: string) => {
      const history = existingLog ? existingLog.interactions : [];
      let prompt = "";
      
      if (userInput) {
          prompt = userInput;
      } else {
          switch(triggerType) {
              case 'initial': prompt = "Give me a small hint to get started, but don't give the answer."; break;
              case 'concept': prompt = "Explain the underlying concept simply."; break;
              case 'example': prompt = "Give me a similar example to practice."; break;
              case 'resources': prompt = "Find 2-3 helpful external resources (links) for this topic."; break;
              case 'question': prompt = userInput || "I have a question."; break;
          }
      }

      const context = `You are a helpful AI Tutor for a student named ${studentName}.
      Task Subject: ${task.subject}
      Task Description: ${task.description}
      Current History: ${JSON.stringify(history.map(h => ({ role: h.isStudentInput ? 'student' : 'tutor', content: h.isStudentInput ? h.prompt : h.response })))}
      
      Goal: Answer the student's next request. Be encouraging, concise, and helpful. NEVER give the direct answer to the homework problem. Guide them.
      Student Request: "${prompt}"`;

      try {
          const result = await generateContentWithRetry({
              model: 'gemini-3-flash-preview',
              contents: context,
              config: { systemInstruction: "You are a friendly and encouraging tutor." }
          });
          
          const responseText = result.text || "I'm having trouble thinking right now. Try again?";
          
          const newInteraction = {
              level: history.length + 1,
              prompt: prompt,
              response: responseText,
              timestamp: new Date(),
              isStudentInput: !!userInput || triggerType === 'question'
          };

          const newHistory = [...history, newInteraction];

          if (existingLog) {
              await updateDocument('tutorLogs', existingLog.id, { interactions: newHistory });
          } else {
              await addDocument('tutorLogs', {
                  studentId,
                  homeworkId,
                  taskId,
                  task,
                  interactions: newHistory
              });
          }

      } catch (e) {
          console.error("Tutor Error", e);
      }
  };

  const handleTranslateTutorInteraction = async (logId: string, interactionIndex: number, text: string, targetLanguage: string) => {
      // Placeholder for persistence logic if needed
  };

  const handleGenerateFamilyConnectPrompt = async () => {
      if (!currentUser) return;
      const prompt = `Generate a family conversation prompt for parents to discuss with their children.
      Focus on one of these categories: EQ, Ethics, Resilience, PlanetProtectors.
      Return JSON: { category: string, prompt: string, parentTip: string }`;

      try {
          const res = await generateContentWithRetry({
                model: 'gemini-3-flash-preview',
                contents: prompt,
                config: { responseMimeType: 'application/json' }
          });
          let data;
          try {
             data = JSON.parse(res.text);
          } catch(e) {
             // Basic fallback parsing if JSON is wrapped in markdown
             const match = res.text.match(/\{[\s\S]*\}/);
             if (match) data = JSON.parse(match[0]);
          }
          
          if(!data) return;

          const studentId = students.find(s => s.parentId === currentUser.id)?.id;
          if (!studentId) return;

          await addDocument('familyConnectPrompts', {
              parentId: currentUser.id,
              studentId: studentId,
              date: new Date().toISOString().split('T')[0],
              category: data.category || 'General',
              prompt: data.prompt,
              parentTip: data.parentTip,
              source: 'general',
              generatedAt: new Date()
          });

      } catch (e) {
          console.error(e);
      }
  };

  // Assessment Handlers
  const handleSaveAssessmentEvent = async (event: Omit<AssessmentEvent, 'id'> | AssessmentEvent) => {
      if ((event as AssessmentEvent).id) {
          await updateDocument('assessmentEvents', (event as AssessmentEvent).id, event);
      } else {
          await addDocument('assessmentEvents', event);
      }
  };

  const handleSaveAssessmentResult = async (result: Omit<AssessmentResult, 'id'> | AssessmentResult) => {
      if ((result as AssessmentResult).id) {
          await updateDocument('assessmentResults', (result as AssessmentResult).id, result);
      } else {
          await addDocument('assessmentResults', result);
      }
  };

  // Render
  // Block rendering until basic auth/profile profile loading is done.
  // This prevents the "User Type Selection" flash because !currentUser is checked later.
  if (authLoading || profileLoading) return <LoadingScreen />;
  
  if (!firebaseUser) {
      return <LandingPage />;
  }

  // If no current user or missing role/org for principal, they need to onboard.
  // We know profileLoading is false, so if currentUser is null here or incomplete, guide them to OnboardingFlow.
  if (!currentUser || !currentUser.role || (currentUser.role === 'principal' && !currentUser.orgId && !simulatedView)) {
      return (
        <OnboardingFlow 
            onComplete={async (profileData, code) => {
                const newUser = { 
                    ...profileData, 
                    id: firebaseUser.uid, 
                    email: firebaseUser.email || '', 
                    googleId: firebaseUser.uid,
                    invitationCodeUsed: code ? code.code : profileData.invitationCodeUsed,
                    invitationCodeId: code ? code.id : profileData.invitationCodeId,
                } as UserProfile;
                await setDocument('users', firebaseUser.uid, newUser);
                setCurrentUser(newUser);
                if (code && profileData.role === 'teacher') {
                    await updateDocument('invitationCodes', code.id, { 
                        isUsed: true, 
                        usedByUserId: newUser.id,
                        usedByName: newUser.name || '',
                        usedByEmail: newUser.email || '',
                        usedAt: new Date().toISOString()
                    });
                }
                return newUser;
            }}
            onCreateOrg={async (name, type, address, photoUrl, adminId, phone, verification) => {
                 const cleanAddress = { ...address };
                 const finalPhone = typeof phone === 'string' ? phone : (cleanAddress as any)?.phone || '';
                 delete (cleanAddress as any).phone;
                 
                 const createdOrg = await handleCreateOrg(name, type, cleanAddress, photoUrl, adminId, finalPhone, verification);
                 // Pre-populate organizations state immediately to prevent snapshot race condition
                 setOrganizations(prev => prev.some(o => o.id === createdOrg.id) ? prev : [...prev, createdOrg]);
                 return createdOrg;
            }}
            onUpdateUser={handleUpdateUser}
            onBack={() => signOut(auth)}
            prefilledData={{ 
                ...currentUser, 
                name: currentUser?.name || firebaseUser.displayName || '', 
                email: currentUser?.email || firebaseUser.email || '', 
                photoUrl: currentUser?.photoUrl || firebaseUser.photoURL || '' 
            }}
            invitationCodes={[]} 
        />
      );
  }

  // === DATA LOADING GATE ===
  // If we have a user, but app data (orgs, classes, etc.) hasn't loaded yet,
  // show loading screen to prevent "Org not found" or empty dashboard flashes.
  if (appDataLoading) {
      return <LoadingScreen />;
  }

  // Super Admin Route Check
  const isSuperAdmin = currentUser.role === 'super_admin' || (firebaseUser?.email ? ROOT_SUPER_ADMIN_EMAILS.includes(firebaseUser.email) : false);

  if (isSuperAdmin && !simulatedView) {
      return (
          <SuperAdminManager
              currentUser={currentUser}
              organizations={organizations}
              users={users}
              classes={classes}
              students={students}
              onApproveOrg={handleApproveOrg}
              onRejectOrg={handleRejectOrg}
              onSuspendOrg={handleSuspendOrg}
              onReactivateOrg={handleReactivateOrg}
              onUpdateOrg={handleUpdateOrg}
              onUpdateUserRole={handleUpdateUserRole}
              onReassignOrgAdmin={handleTransferOwnership}
              onSwitchView={(role, orgId) => setSimulatedView({ role, orgId })}
              onLogout={() => signOut(auth)}
          />
      );
  }

  const effectiveRole = simulatedView?.role || currentUser.role;
  const effectiveOrgId = simulatedView?.orgId || currentUser.orgId;

  if (effectiveRole === 'principal') {
      const myOrg = organizations.find(o => o.id === effectiveOrgId);
      if (!myOrg) {
          return (
              <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50 text-center space-y-4">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 mb-2">
                      <span className="material-icons-round text-3xl">domain</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Institution Record Pending</h3>
                  <p className="text-gray-500 max-w-md text-sm">We couldn't locate an active institution profile attached to this session. You can complete your institution registration or return to the console.</p>
                  <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                      <button 
                          onClick={async () => {
                              if (currentUser) {
                                  await updateDocument('users', currentUser.id, { orgId: '' });
                                  setCurrentUser(prev => prev ? { ...prev, orgId: undefined } : null);
                              }
                          }}
                          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-sm transition-colors flex items-center gap-2"
                      >
                          <span className="material-icons-round text-sm">add_business</span>
                          Complete Institution Setup
                      </button>
                      <button 
                          onClick={() => signOut(auth)}
                          className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl text-sm font-bold transition-colors"
                      >
                          Sign Out
                      </button>
                      {simulatedView && (
                          <button 
                              onClick={() => setSimulatedView(null)}
                              className="px-5 py-2.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-xl text-sm font-bold transition-colors"
                          >
                              Return to Super Admin Console
                          </button>
                      )}
                  </div>
              </div>
          );
      }

      // Check organization approval status (Anti-Spam gating)
      const orgStatus = myOrg.status || 'pending';
      if (orgStatus !== 'active') {
          return (
              <div className="relative min-h-screen bg-gray-50 flex flex-col">
                  {simulatedView && (
                      <div className="bg-amber-600 text-white px-4 py-2.5 text-xs font-bold flex items-center justify-between shadow-md z-50">
                          <span className="flex items-center gap-1.5">
                              <span className="material-icons-round text-sm">visibility</span>
                              Simulated Principal View ({myOrg.name} — Status: {orgStatus.toUpperCase()})
                          </span>
                          <button 
                              onClick={() => setSimulatedView(null)} 
                              className="bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-xs transition-colors"
                          >
                              Exit Preview
                          </button>
                      </div>
                  )}
                  <PendingOrgScreen
                      org={myOrg}
                      user={currentUser}
                      onUpdateOrg={handleUpdateOrg}
                      onUpdateUser={handleUpdateUser}
                      onLogout={() => {
                          if (simulatedView) setSimulatedView(null);
                          else signOut(auth);
                      }}
                  />
              </div>
          );
      }

      return (
          <div className="relative min-h-screen flex flex-col">
              {simulatedView && (
                  <div className="bg-indigo-700 text-white px-4 py-2.5 text-xs font-bold flex items-center justify-between shadow-md z-50">
                      <span className="flex items-center gap-1.5">
                          <span className="material-icons-round text-sm">admin_panel_settings</span>
                          Simulated Principal View: <strong>{myOrg.name}</strong>
                      </span>
                      <button 
                          onClick={() => setSimulatedView(null)} 
                          className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs transition-colors"
                      >
                          Return to Super Admin Console
                      </button>
                  </div>
              )}
              <PrincipalManager
                  user={currentUser}
                  org={myOrg}
                  allAcademicYears={academicYears.filter(y => y.orgId === myOrg.id)}
                  activeYear={academicYears.find(y => y.orgId === myOrg.id && y.isActivated)}
                  allUsers={users.filter(u => u.orgId === myOrg.id || u.id === myOrg.adminId || (myOrg.vicePrincipalEmails || []).includes(u.email))} 
                  classes={classes.filter(c => c.orgId === myOrg.id)}
                  students={students} 
                  allMessages={messages}
                  announcements={announcements.filter(a => a.orgId === myOrg.id)}
                  weeklyLogs={weeklyLogs}
                  allHomeworks={homeworks}
                  studentHomeworkStatuses={studentHomeworkStatuses}
                  stuckLogs={stuckLogs}
                  attendanceRecords={attendanceRecords}
                  kudos={kudos.filter(k => classes.find(c => c.id === k.classId)?.orgId === myOrg.id || k.classId === 'GLOBAL')}
                  assessmentEvents={assessmentEvents.filter(e => classes.find(c => c.id === e.classId)?.orgId === myOrg.id)}
                  assessmentResults={assessmentResults}
                  onLogout={() => signOut(auth)}
                  onCreateClass={async (name, yearId) => { await addDocument('classes', { name, academicYearId: yearId, orgId: myOrg.id, teacherIds: [] }) }}
                  onUpdateClass={async (id, data) => { await updateDocument('classes', id, data) }}
                  onDeleteClass={async (id) => { await deleteDocument('classes', id) }}
                  onSendAnnouncement={async (title, text, img, ids, yearId) => handleSendAnnouncement(title, text, img, ids, yearId, myOrg.id)}
                  onAssignTeachers={async (clsId, tIds, ctId) => { await updateDocument('classes', clsId, { teacherIds: tIds, classTeacherId: ctId }) }}
                  onUpdateUser={handleUpdateUser}
                  onUpdateOrg={handleUpdateOrg}
                  onSendMessage={handleSendMessage}
                  onUpdateMessage={handleUpdateMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onUpdateAnnouncement={async (id, data) => { await updateDocument('announcements', id, data) }}
                  onDeleteAnnouncement={async (id) => { await deleteDocument('announcements', id) }}
                  setViewingImageUrl={setViewingImageUrl}
                  onCreateClasses={async (newClasses, yearId) => { 
                      const created = [];
                      for(const c of newClasses) {
                          created.push(await addDocument('classes', { ...c, orgId: myOrg.id, teacherIds: [], academicYearId: yearId }));
                      }
                      return created;
                  }}
                  onUpdateStudentStatus={async (ids, status) => { 
                      for(const id of ids) await updateDocument('students', id, { approvalStatus: status });
                  }}
                  onMoveStudents={async (ids, cid, yid) => {
                      for(const id of ids) await updateDocument('students', id, { classId: cid, academicYearId: yid });
                  }}
                  onCreateOrUpdateAcademicYear={async (year) => {
                      if ((year as any).id) {
                          await updateDocument('academicYears', (year as any).id, year);
                          return year;
                      }
                      return await addDocument('academicYears', { ...year, orgId: myOrg.id });
                  }}
                  onDeleteAcademicYear={async (id) => {
                      try {
                          // Cascade delete classes belonging to this academic year
                          const yearClasses = classes.filter(c => c.academicYearId === id);
                          for (const cls of yearClasses) {
                              await deleteDocument('classes', cls.id);
                          }
                          await deleteDocument('academicYears', id);
                          // Optimistically update local state for immediate UI feedback
                          setAcademicYears(prev => prev.filter(y => y.id !== id));
                          if (yearClasses.length > 0) {
                              setClasses(prev => prev.filter(c => c.academicYearId !== id));
                          }
                      } catch (error) {
                          console.error("Failed to delete academic year:", error);
                          throw error;
                      }
                  }}
                  onActivateYear={async (id) => {
                      const others = academicYears.filter(y => y.orgId === myOrg.id && y.id !== id);
                      for(const y of others) if(y.isActivated) await updateDocument('academicYears', y.id, { isActivated: false });
                      await updateDocument('academicYears', id, { isActivated: true });
                  }}
                  onGenerateInvitationCodes={async (type, count, orgId, classId) => {
                      const codes: InvitationCode[] = [];
                      const now = new Date();
                      for(let i=0; i<count; i++) {
                          const codeStr = Math.random().toString(36).substring(2, 8).toUpperCase();
                          const newCode = await addDocument<InvitationCode>('invitationCodes', {
                              code: codeStr, type, orgId, classId, isUsed: false, createdAt: now
                          });
                          codes.push(newCode);
                      }
                      return codes;
                  }}
                  onCreatePendingTeacher={async (name, email, orgId) => { 
                      // Stub: Typically sends email or creates pre-auth record. 
                      console.log("Invite teacher:", name, email); 
                  }}
                  invitationCodes={invitationCodes.filter(c => c.orgId === myOrg.id)}
                  calendarEvents={calendarEvents}
                  onCreateOrUpdateCalendarEvent={async (evt) => {
                      if ((evt as any).id) await updateDocument('calendarEvents', (evt as any).id, evt);
                      else await addDocument('calendarEvents', evt);
                  }}
                  onDeleteCalendarEvent={async (id) => await deleteDocument('calendarEvents', id)}
                  onSendKudo={handleSendKudo}
                  tutorLogs={tutorLogs}
                  onDeleteAccount={handleDeleteAccount}
                  onMarkMessagesRead={handleMarkMessagesRead}
                  onSaveAssessmentEvent={handleSaveAssessmentEvent}
                  onSaveAssessmentResult={handleSaveAssessmentResult}
                  onSwitchToTeacherMode={() => setSimulatedView({ role: 'teacher', orgId: myOrg.id })}
                  onTransferOwnership={handleTransferOwnership}
              />
          </div>
      );
  }

  if (effectiveRole === 'teacher') {
      const myOrg = organizations.find(o => o.id === effectiveOrgId);
      if (!myOrg) return <div className="p-10 text-center">Organization not found.</div>;
      
      const activeYear = academicYears.find(y => y.orgId === myOrg.id && y.isActivated);

      return (
          <div className="relative min-h-screen flex flex-col">
              {simulatedView && (
                  <div className="bg-indigo-900 text-white px-4 py-2.5 text-xs font-bold flex items-center justify-between shadow-md z-50 sticky top-0">
                      <span className="flex items-center gap-2">
                          <span className="material-icons-round text-base text-indigo-300">school</span>
                          <span>Teaching Workspace: <strong>{currentUser.name}</strong> ({myOrg.name})</span>
                      </span>
                      <button 
                          onClick={() => setSimulatedView(null)} 
                          className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 cursor-pointer font-bold border border-white/20"
                      >
                          <span className="material-icons-round text-xs">admin_panel_settings</span>
                          Return to Admin Console
                      </button>
                  </div>
              )}
              <TeacherManager
              user={currentUser}
              allUsers={users}
              classes={classes.filter(c => c.orgId === myOrg.id)}
              org={myOrg}
              allAcademicYears={academicYears.filter(y => y.orgId === myOrg.id)}
              activeYear={activeYear}
              allMessages={messages}
              allLogs={weeklyLogs}
              allStudents={students.filter(s => {
                  const cls = classes.find(c => c.id === s.classId);
                  return cls?.orgId === myOrg.id;
              })}
              allAnnouncements={announcements}
              studentHomeworkStatuses={studentHomeworkStatuses}
              stuckLogs={stuckLogs}
              kudos={kudos}
              assessmentEvents={assessmentEvents.filter(e => {
                  const cls = classes.find(c => c.id === e.classId);
                  return cls?.orgId === myOrg.id;
              })}
              assessmentResults={assessmentResults}
              onUpdateStudentStatus={async (id, status) => await updateDocument('students', id, { approvalStatus: status })}
              onSendMessage={handleSendMessage}
              onUpdateMessage={handleUpdateMessage}
              onDeleteMessage={handleDeleteMessage}
              onMarkMessagesRead={handleMarkMessagesRead}
              onLogout={() => signOut(auth)}
              onUpdateUser={handleUpdateUser}
              setViewingImageUrl={setViewingImageUrl}
              allHomeworks={homeworks}
              onCreateHomework={async (hw) => {
                  let finalAttachments = hw.attachments || [];
                  if (finalAttachments.length > 0) {
                      finalAttachments = await Promise.all(finalAttachments.map(async (att: string, idx: number) => {
                          if (att && att.startsWith('data:')) {
                              const path = `homework/${currentUser?.id || 'teacher'}_${Date.now()}_${idx}`;
                              return await uploadImageFromBase64(att, path);
                          }
                          return att;
                      }));
                  }
                  await addDocument('homeworks', { ...hw, attachments: finalAttachments, timestamp: new Date() });
              }}
              onUpdateHomework={async (id, tasks, date, attachments) => {
                  let finalAttachments = attachments;
                  if (finalAttachments && finalAttachments.length > 0) {
                      finalAttachments = await Promise.all(finalAttachments.map(async (att: string, idx: number) => {
                          if (att && att.startsWith('data:')) {
                              const path = `homework/${currentUser?.id || 'teacher'}_${Date.now()}_${idx}`;
                              return await uploadImageFromBase64(att, path);
                          }
                          return att;
                      }));
                  }
                  await updateDocument('homeworks', id, { 
                      tasks, 
                      date, 
                      ...(finalAttachments !== undefined ? { attachments: finalAttachments } : {}) 
                  });
              }}
              onDeleteHomework={async (id) => await deleteDocument('homeworks', id)}
              onGenerateInvitationCodes={async (type, count, orgId, classId) => {
                   const codes: InvitationCode[] = [];
                   const now = new Date();
                   for(let i=0; i<count; i++) {
                       const codeStr = Math.random().toString(36).substring(2, 8).toUpperCase();
                       const newCode = await addDocument<InvitationCode>('invitationCodes', {
                           code: codeStr, type, orgId, classId, isUsed: false, createdAt: now
                       });
                       codes.push(newCode);
                   }
                   return codes;
              }}
              attendanceRecords={attendanceRecords}
              preReportedAbsences={preReportedAbsences}
              onSaveAttendance={async (records) => {
                  for (const r of records) {
                      const existing = attendanceRecords.find(ex => ex.studentId === r.studentId && ex.date === r.date && ex.classId === r.classId);
                      if(existing) await updateDocument('attendanceRecords', existing.id, r);
                      else await addDocument('attendanceRecords', r);
                  }
              }}
              calendarEvents={calendarEvents}
              onCreateOrUpdateCalendarEvent={async (evt) => {
                  if ((evt as any).id) await updateDocument('calendarEvents', (evt as any).id, evt);
                  else await addDocument('calendarEvents', evt);
              }}
              onDeleteCalendarEvent={async (id) => await deleteDocument('calendarEvents', id)}
              onSendKudo={handleSendKudo}
              tutorLogs={tutorLogs}
              classInsights={classInsights}
              onSaveClassInsight={async (insight) => {
                  const existing = classInsights.find(i => i.classId === insight.classId);
                  if (existing) await updateDocument('classInsights', existing.id, insight);
                  else await addDocument('classInsights', insight);
              }}
              onDeleteAccount={handleDeleteAccount}
              onSaveAssessmentEvent={handleSaveAssessmentEvent}
              onSaveAssessmentResult={handleSaveAssessmentResult}
              invitationCodes={invitationCodes.filter(c => c.orgId === myOrg.id)}
          />
          </div>
      );
  }

  if (effectiveRole === 'parent') {
      return (
          <ParentManager
              user={currentUser}
              allUsers={users}
              students={students.filter(s => s.parentId === currentUser.id && s.approvalStatus !== 'removed')}
              allStudents={students} 
              classes={classes}
              academicYears={academicYears}
              allMessages={messages}
              onMarkMessagesRead={handleMarkMessagesRead}
              allAnnouncements={announcements}
              allLogs={weeklyLogs}
              onAddStudent={async (childName, codeStr, gender, photoUrl) => {
                  try {
                      if (!photoUrl) {
                          return { success: false, message: "Child photo is mandatory. Please upload a photo." };
                      }
                      if (!childName || !childName.trim()) {
                          return { success: false, message: "Child's name is required." };
                      }
                      if (!codeStr || !codeStr.trim()) {
                          return { success: false, message: "Invitation code is required." };
                      }

                      const trimmedCode = codeStr.trim().toUpperCase();
                      let cls = classes.find(c => c.id === trimmedCode || c.name === trimmedCode); 
                      let usedInviteCode: InvitationCode | undefined;
                      
                      if (!cls) {
                          const allCodesSnapshot = await getDocs(collection(db, "invitationCodes"));
                          const allCodes = allCodesSnapshot.docs.map(d => ({id: d.id, ...d.data()}) as InvitationCode);
                          const inviteData = allCodes.find(c => c.code === trimmedCode);
                          
                          if (!inviteData) {
                              return { success: false, message: "Invalid Code: Code not found." };
                          }
                          if (inviteData.type !== 'parent') {
                              return { success: false, message: "Invalid Code: This code is not for parents." };
                          }
                          if (inviteData.isUsed) {
                              return { success: false, message: "Invalid Code: Code has already been used." };
                          }
                          
                          cls = classes.find(c => c.id === inviteData.classId);
                          
                          if (!cls && inviteData.classId) {
                              const classDocRef = doc(db, 'classes', inviteData.classId);
                              const classDoc = await getDoc(classDocRef);
                              if (classDoc.exists()) {
                                  cls = { id: classDoc.id, ...classDoc.data() } as ClassRoom;
                              }
                          }

                          if (!cls) {
                              return { success: false, message: "Invalid Code: Associated class no longer exists." };
                          }
                          
                          usedInviteCode = inviteData;
                      }

                      let finalPhotoUrl = photoUrl;
                      if (photoUrl && photoUrl.startsWith('data:')) {
                          const fileName = `${currentUser.id}_${Date.now()}`;
                          const path = `student_photos/${fileName}`;
                          finalPhotoUrl = await uploadImageFromBase64(photoUrl, path);
                      }

                      const newStudentRef = await addDocument('students', {
                          parentId: currentUser.id,
                          classId: cls.id,
                          childName,
                          approvalStatus: 'pending',
                          academicYearId: cls.academicYearId || '',
                          gender: gender || 'boy',
                          photoUrl: finalPhotoUrl,
                          invitationCodeUsed: usedInviteCode ? usedInviteCode.code : undefined,
                          invitationCodeId: usedInviteCode ? usedInviteCode.id : undefined,
                          enrolledAt: new Date().toISOString()
                      });
                      
                      if (usedInviteCode) {
                          await updateDocument('invitationCodes', usedInviteCode.id, { 
                              isUsed: true, 
                              usedByUserId: currentUser.id,
                              usedByName: currentUser.name || '',
                              usedByEmail: currentUser.email || '',
                              studentId: (newStudentRef as any)?.id || undefined,
                              studentName: childName.trim(),
                              usedAt: new Date().toISOString()
                          });
                      }

                      if (!currentUser.orgId && cls.orgId) {
                         await updateDocument('users', currentUser.id, { orgId: cls.orgId });
                      }

                      return { success: true };
                  } catch (err: any) {
                      console.error("Add child error:", err);
                      return { success: false, message: `System Error: ${err.message}` };
                  }
              }}
              onSendMessage={handleSendMessage}
              onUpdateMessage={handleUpdateMessage}
              onDeleteMessage={handleDeleteMessage}
              onSaveWeeklyLog={handleSaveWeeklyLog}
              onLogout={() => signOut(auth)}
              onUpdateUser={handleUpdateUser}
              onUpdateStudent={handleUpdateStudent}
              onDeleteStudent={handleDeleteStudent}
              setViewingImageUrl={setViewingImageUrl}
              orgs={organizations}
              allHomeworks={homeworks}
              studentHomeworkStatuses={studentHomeworkStatuses}
              onUpdateStudentHomeworkStatus={handleUpdateStudentHomeworkStatus}
              onLogStuck={handleLogStuck}
              attendanceRecords={attendanceRecords}
              onReportAbsence={handleManageAbsence}
              calendarEvents={calendarEvents}
              tutorLogs={tutorLogs}
              onGetTutorHelp={handleGetTutorHelp}
              onTranslateTutorInteraction={handleTranslateTutorInteraction}
              familyConnectPrompts={familyConnectPrompts}
              onGenerateFamilyConnectPrompt={handleGenerateFamilyConnectPrompt}
              allKudos={kudos}
              onSendKudo={handleSendKudo}
              onDeleteAccount={handleDeleteAccount}
              assessmentEvents={assessmentEvents}
              assessmentResults={assessmentResults}
              preReportedAbsences={preReportedAbsences}
              stuckLogs={stuckLogs}
          />
      );
  }
  
  return (
    <div className="h-screen flex items-center justify-center flex-col gap-4">
        <p>Unknown Role</p>
        <button onClick={() => signOut(auth)}>Sign Out</button>
    </div>
  );
};
